import React from 'react';
import { orderBy } from 'lodash';
import { useToggle } from 'usehooks-ts';
import { useParams } from 'react-router-dom';
import { Label } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';
import { drawDOM, exportImage } from '@progress/kendo-drawing';
import { Checkbox, Input, RadioButton } from '@progress/kendo-react-inputs';

import {
  ReelsLeaderboardFirst,
  ReelsLeaderboardSecond,
  ReelsLeaderboardThird,
  ReelsLeaderboardTitle
} from '../../../../assets';
import { uuid } from '../../../../types/common-helper';
import { GameContext } from '../../../../contexts/game';
import { Timeline, useReels } from '../../../../contexts/reels';
import { useUploadReelsResources } from '../../../../hooks/use-reels';
import { GameDocumentContext } from '../../../../contexts/game-document';

const LeaderboardItem: React.FC<{
  isShowScore: boolean;
  name: string;
  score: number;
  index: number;
}> = ({ index, isShowScore, name, score }) => {
  return (
    <div
      className={'d-flex align-items-center justify-content-between'}
      style={{
        backgroundColor: '#F8FCFE',
        borderRadius: '8px'
      }}>
      <div
        className={'d-flex align-items-center gap-2'}
        style={{
          fontSize: '20px'
        }}>
        <div
          className={
            'd-flex align-items-center justify-content-center p-2 fw-bold'
          }
          style={{
            width: '40px',
            height: '40px'
          }}>
          {index === 0 && <ReelsLeaderboardFirst color={'#FFD700'} />}
          {index === 1 && <ReelsLeaderboardSecond />}
          {index === 2 && <ReelsLeaderboardThird />}
          {index >= 3 && <span>{index + 1}</span>}
        </div>
        <span style={{ fontWeight: 'bold' }}>{name}</span>
      </div>

      <div
        style={{
          display: 'flex',
          padding: '16px',
          color: '#4698CB',
          alignItems: 'center',
          flexDirection: 'column',
          justifyContent: 'center',
          opacity: isShowScore ? 1 : 0
        }}>
        <span style={{ fontWeight: 'bold' }}>{score ?? 0}</span>
        <span style={{ color: '#003A64', fontSize: '12px' }}>points</span>
      </div>
    </div>
  );
};

export const Leaderboard: React.FC = () => {
  const { gameCode } = useParams();
  const [game] = React.useContext(GameContext);
  const [gameDocument] = React.useContext(GameDocumentContext);

  const [showScore, setShowScore] = useToggle(true);
  const ref = React.useRef<HTMLDivElement>(null);
  const [leaderboardForm, setLeaderboardForm] = React.useState({
    duration: 0,
    errorMessage: ''
  });
  const [displayCount, setDisplayCount] = React.useState(3);

  const { timeline, handleChange } = useReels();
  const { isMutating, trigger } = useUploadReelsResources(gameCode ?? '');

  const addToTimeline = async () => {
    if (!ref.current) return;

    try {
      setLeaderboardForm((prev) => ({ ...prev, errorMessage: '' }));
      const clone = ref.current.cloneNode(true) as HTMLElement;
      const container = document.createElement('div');
      const title = document.createElement('span');
      title.innerHTML = 'Leaderboard';
      title.style.fontWeight = 'bold';
      title.style.fontSize = '18px';
      container.style.textAlign = 'center';
      container.style.display = 'flex';
      container.style.flexDirection = 'column';
      container.style.gap = '24px';
      container.style.padding = '24px';
      container.style.backgroundColor = 'white';
      container.style.position = 'fixed';
      container.style.width = '500px';
      container.style.left = '-10000px';
      container.style.top = '0';

      container.append(title, clone);
      document.body.appendChild(container);
      const group = await drawDOM(container, {
        scale: 3
      });

      const dataUrl = await exportImage(group);
      const blob = await (await fetch(dataUrl)).blob();
      const newFile = new File(
        [blob],
        `leaderboard-${gameCode}-${new Date().toISOString()}.png`,
        { type: 'image/png' }
      );
      trigger(
        {
          gameCode: gameCode ?? '',
          files: [newFile]
        },
        {
          onSuccess: (result) => {
            document.body.removeChild(container);
            if (result.length && result[0]?.url) {
              const newItem: Timeline = {
                id: uuid(),
                url: result[0].url,
                duration_secs: leaderboardForm.duration,
                type: 'image'
              };

              handleChange({ timeline: [...timeline, newItem] });
            }
          }
        }
      );
    } catch (err) {
      console.error('Export failed', err);
      setLeaderboardForm((prev) => ({
        ...prev,
        errorMessage: 'Export failed, please try again.'
      }));
    }
  };

  return (
    <div className={'reels-leaderboard-container pb-2'}>
      <div
        className={'leaderboard-form'}
        style={{
          gap: '24px',
          padding: '24px',
          color: '#003A64',
          borderRadius: '8px',
          backgroundColor: 'white',
          border: '1px solid #A5B7BF'
        }}>
        <div className={'d-flex gap-3 align-items-center'}>
          <ReelsLeaderboardTitle fontSize={24} />
          <b
            style={{
              fontSize: '18px'
            }}>
            Event Leaderboard
          </b>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            padding: '16px',
            borderRadius: '8px',
            backgroundColor: '#F8FCFE'
          }}>
          <Button
            themeColor={'primary'}
            className={'w-100'}
            onClick={addToTimeline}
            disabled={isMutating}>
            Add to timeline
          </Button>
          {leaderboardForm.errorMessage && (
            <span
              className={'text-danger'}
              style={{
                fontSize: '12px'
              }}>
              {leaderboardForm.errorMessage}
            </span>
          )}
        </div>

        <div
          style={{
            borderRadius: '8px',
            backgroundColor: '#F8FCFE',
            padding: '16px'
          }}>
          <Checkbox
            label={'Include score'}
            checked={showScore}
            disabled={isMutating}
            onChange={setShowScore}
          />
        </div>

        <div
          className={'d-flex flex-column gap-2'}
          style={{
            borderRadius: '8px',
            backgroundColor: '#F8FCFE',
            padding: '16px'
          }}>
          <div className={'d-flex gap-1 align-items-center'}>
            <RadioButton
              name={'display-count'}
              id={'top-3'}
              value={3}
              checked={displayCount === 3}
              onChange={() => setDisplayCount(3)}
            />
            <Label
              className={'cursor-pointer'}
              editorId={
                'top-3'
              }>{`Top 3 ${gameDocument.gameDocument?.settings.preGame.isPreGameTeamRequired ? 'Teams' : 'Players'}`}</Label>
          </div>
          <div className={'d-flex gap-1 align-items-center'}>
            <RadioButton
              name={'display-count'}
              id={'top-10'}
              value={10}
              checked={displayCount === 10}
              onChange={() => setDisplayCount(10)}
            />
            <Label
              className={'cursor-pointer'}
              editorId={
                'top-10'
              }>{`Top 10 ${gameDocument.gameDocument?.settings.preGame.isPreGameTeamRequired ? 'Teams' : 'Players'}`}</Label>
          </div>
        </div>

        <div>
          <Label>Leaderboard Duration (seconds)</Label>
          <Input
            type={'number'}
            disabled={isMutating}
            value={leaderboardForm.duration ?? 0}
            min={0}
            onChange={(e) =>
              setLeaderboardForm((prev) => ({
                ...prev,
                duration: parseInt(e.value)
              }))
            }
          />
        </div>

        <div className={'d-flex flex-column'} style={{ gap: '12px' }} ref={ref}>
          {/* using team if team is required (team game) */}
          {gameDocument.gameDocument?.settings.preGame.isPreGameTeamRequired &&
            orderBy(game.gameState?.teams, ['score'], ['desc'])
              .slice(0, displayCount)
              .map((team, idx) => (
                <LeaderboardItem
                  key={team.code}
                  index={idx}
                  isShowScore={showScore}
                  name={team.name ?? ''}
                  score={team.score ?? 0}
                />
              ))}

          {/* using player instead if team not required */}
          {!gameDocument.gameDocument?.settings.preGame.isPreGameTeamRequired &&
            orderBy(game.gameState?.players, ['score'], ['desc'])
              .slice(0, displayCount)
              .map((player, idx) => (
                <LeaderboardItem
                  key={player.code}
                  index={idx}
                  isShowScore={showScore}
                  name={player.name ?? ''}
                  score={player.score ?? 0}
                />
              ))}
        </div>
      </div>
    </div>
  );
};
